import type { CustomAttr } from '@api/share-list'
import type { Catalog } from '@model/pricelist/Catalog'
import type { Currency } from '@model/product/PriceInfo'
import type { Locale } from '@model/locales'
import type { WishlistItem } from '@model/wishlist/WishlistItem'
import type { deliveries } from '@components/profile/Wishlist/PlatinumLoungeContent'

interface GenerateInput {
  catalogTranslation: string
  showAlcoholWarning: boolean
  locale: Locale
  catalogId: Catalog
  listTotal: number
  currency: Currency
  itemsList: WishlistItem[]
  isPlatinumLoungeOrder?: boolean
  platinumOrderName?: string
  platinumOrderCO?: string
  platinumOrderDeliveryPoint?: typeof deliveries[number]
}

interface ItemData {
  id: number
  productId: number
  activeRoute: Catalog
  quantity: number
  language: Locale
  route: Catalog
  topPrice: number
  youSave: number
  youSavePercent: number
  showPricePerLitre: boolean
  nonAlcoholicProduct: boolean
  showNormalPrice: boolean
  showLandPrice: boolean
  showYouSave: boolean
  prices: {
    normal: number
    land: 0
    top: number
    litre: number
  }
  sku: string
}

export const generateCustAttrsForWishlist = ({
  catalogTranslation,
  showAlcoholWarning,
  locale,
  catalogId,
  listTotal,
  currency,
  itemsList,
  isPlatinumLoungeOrder = false,
  platinumOrderName,
  platinumOrderCO,
  platinumOrderDeliveryPoint,
}: GenerateInput): CustomAttr[] => {
  const wishlistData = itemsList.map(transformItem(catalogId, locale))

  return [
    {
      name: 'route',
      value: isPlatinumLoungeOrder ? `Platinum Lounge ${catalogTranslation}` : catalogTranslation,
    },
    {
      name: 'wishlistData',
      value: JSON.stringify(wishlistData),
    },
    {
      name: 'showAlcoholWarning',
      value: showAlcoholWarning,
    },
    {
      name: 'productLink',
      value: `${window.origin}/${locale}/${catalogId}/product/`,
    },
    {
      name: 'linkTail',
      value: '',
    },
    {
      name: 'listTotal',
      value: listTotal,
    },
    {
      name: 'currencyCode',
      value: currency,
    },
    ...(isPlatinumLoungeOrder && platinumOrderDeliveryPoint
      ? [
          {
            name: 'extra',
            value: `${platinumOrderName}, ${platinumOrderCO}`,
          },
          {
            name: 'deliveryPoint',
            value: platinumOrderDeliveryPoint,
          },
        ]
      : []),
  ]
}

const transformItem =
  (catalogId: Catalog, locale: Locale) =>
  (item: WishlistItem): ItemData => {
    return {
      id: item.id,
      productId: item.productId,
      activeRoute: catalogId || 'shop',
      language: locale,
      nonAlcoholicProduct: Boolean(item.product?.priceInfo?.litrePriceShow) || false,
      youSave: item.product?.priceInfo?.youSaveAmount || 0,
      youSavePercent: item.product?.priceInfo?.youSavePercent || 0,
      route: catalogId || 'shop',
      quantity: item.quantityDesired,
      sku: item.sku,
      prices: {
        normal: item.product?.priceInfo?.normalPrice || 0,
        land: 0,
        top: item.product?.priceInfo?.topPrice || 0,
        litre: item.product?.priceInfo?.litrePrice || 0,
      },
      showLandPrice: false,
      showNormalPrice: item.product?.priceInfo?.normalPriceShow || true,
      showPricePerLitre: item.product?.priceInfo?.litrePriceShow,
      topPrice: item.product?.priceInfo?.topPrice || 0,
      showYouSave: Boolean(item.product?.priceInfo?.youSavePercentShow),
    }
  }
